/**
 * Portfolio Sync Hook
 * Synchronizes portfolio data with real-time asset prices using the unified API
 */

import { useEffect, useCallback, useRef } from 'react';
import { usePortfolioStore } from '../stores/portfolioStore';
import { useAssetStore } from '../stores/assetStore';
import { useLoadingState } from './useLoadingState';
import { useErrorHandler } from './useErrorHandler';
import { unifiedApiService } from '../services/unifiedApi';
import { useAppVisibility } from './useAppVisibility';

interface UsePortfolioSyncOptions {
  autoRefresh?: boolean;
  refreshInterval?: number; // in seconds
}

export const usePortfolioSync = (options: UsePortfolioSyncOptions = {}) => {
  const { 
    portfolio, 
    updatePortfolioFromPrices, 
    setLoading, 
    setError 
  } = usePortfolioStore();
  
  const { updateAssetPrice } = useAssetStore();
  const { startLoading: setApiLoading, stopLoading: clearApiLoading } = useLoadingState({ key: 'portfolio-sync' });
  const { handleError } = useErrorHandler();
  const isAppVisible = useAppVisibility();

  const {
    autoRefresh = true,
    refreshInterval = 30,
  } = options;
  
  // Track last successful update time
  const lastSuccessfulUpdateRef = useRef<number | null>(null);
  const intervalTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevVisibilityRef = useRef<boolean>(isAppVisible);

  // Sync portfolio holdings with asset store (currently unused but kept for future use)
  // const syncHoldingsWithAssets = useCallback(() => {
  //   if (!portfolio) return;
  //   // Implementation commented out as it's not currently used
  // }, [portfolio, assets]);

  // Fetch latest prices for portfolio assets
  const fetchPortfolioPrices = useCallback(async () => {
    if (!portfolio || portfolio.holdings.length === 0) return;

    try {
      setLoading(true);
      setApiLoading();
      setError(null);

      const portfolioAssets = portfolio.holdings.map(h => h.asset);
      
      // Use unified API to get latest prices
      const pricePromises = portfolioAssets.map(async (asset) => {
        try {
          const priceData = await unifiedApiService.getAssetPrice(asset, {
            timeout: 10000,
            context: 'portfolio-sync',
          });
          return { assetId: asset.id, price: priceData.current_price };
        } catch (error) {
          console.warn(`Failed to fetch price for ${asset.symbol}:`, error);
          return { assetId: asset.id, price: null };
        }
      });

      const priceResults = await Promise.all(pricePromises);
      
      // Update asset store with new prices
      priceResults.forEach(result => {
        if (result.price !== null) {
          const asset = portfolioAssets.find(a => a.id === result.assetId);
          if (asset) {
            updateAssetPrice(result.assetId, {
              current_price: result.price,
              last_updated: new Date().toISOString(),
            });
          }
        }
      });

      // Create price map for portfolio update
      const assetPrices: Record<string, number> = {};
      priceResults.forEach(result => {
        if (result.price !== null) {
          assetPrices[result.assetId] = result.price;
        }
      });

      // Update portfolio with new prices
      if (Object.keys(assetPrices).length > 0) {
        await updatePortfolioFromPrices(assetPrices);
        // Record successful update time
        lastSuccessfulUpdateRef.current = Date.now();
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch portfolio prices';
      setError(errorMessage);
      handleError(error instanceof Error ? error : new Error(errorMessage), 'portfolio-sync');
    } finally {
      setLoading(false);
      clearApiLoading();
    }
  }, [
    portfolio, 
    setLoading, 
    setApiLoading, 
    clearApiLoading,
    setError, 
    updateAssetPrice, 
    updatePortfolioFromPrices, 
    handleError
  ]);

  // Auto-refresh effect - only when visible
  useEffect(() => {
    if (!autoRefresh || !portfolio) return;
    
    // Clear existing interval
    if (intervalTimerRef.current) {
      clearInterval(intervalTimerRef.current);
      intervalTimerRef.current = null;
    }

    // Only set up auto-refresh when app is visible
    if (isAppVisible) {
      // Check if we need to fetch immediately
      const now = Date.now();
      const lastUpdate = lastSuccessfulUpdateRef.current;
      const intervalMs = refreshInterval * 1000;
      
      if (lastUpdate === null) {
        // Never updated, fetch immediately
        fetchPortfolioPrices();
      } else {
        const timeSinceLastUpdate = now - lastUpdate;
        if (timeSinceLastUpdate >= intervalMs) {
          // Interval has passed, fetch immediately
          fetchPortfolioPrices();
        } else {
          // Wait for remaining time before first fetch
          const remainingTime = intervalMs - timeSinceLastUpdate;
          setTimeout(() => {
            fetchPortfolioPrices();
          }, remainingTime);
        }
      }

      // Set up interval for auto-refresh
      intervalTimerRef.current = setInterval(fetchPortfolioPrices, refreshInterval * 1000);
    }

    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
    };
  }, [autoRefresh, refreshInterval, portfolio, fetchPortfolioPrices, isAppVisible]);
  
  // Handle visibility changes: stop requests when hidden
  useEffect(() => {
    const wasVisible = prevVisibilityRef.current;
    const isNowVisible = isAppVisible;
    
    if (!isNowVisible) {
      // 窗口隐藏：停止所有网络请求
      unifiedApiService.cancelRequests('portfolio-sync');
      // 清除定时器
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
    } else if (!wasVisible && isNowVisible) {
      // 窗口从隐藏变为显示：检查上次成功更新的时间
      const now = Date.now();
      const lastUpdate = lastSuccessfulUpdateRef.current;
      const intervalMs = refreshInterval * 1000;
      
      if (lastUpdate === null) {
        // 从未更新过，立即更新
        fetchPortfolioPrices();
      } else {
        const timeSinceLastUpdate = now - lastUpdate;
        if (timeSinceLastUpdate >= intervalMs) {
          // 时间间隔已到，立即更新
          fetchPortfolioPrices();
        } else {
          // 时间间隔未到，等待剩余时间后再更新
          const remainingTime = intervalMs - timeSinceLastUpdate;
          setTimeout(() => {
            fetchPortfolioPrices();
          }, remainingTime);
        }
      }
      
      // 重新设置定时器
      if (autoRefresh && portfolio) {
        intervalTimerRef.current = setInterval(fetchPortfolioPrices, refreshInterval * 1000);
      }
    }
    
    // 更新可见性状态
    prevVisibilityRef.current = isNowVisible;
  }, [isAppVisible, refreshInterval, autoRefresh, portfolio, fetchPortfolioPrices]);

  // Manual refresh function
  const refreshPrices = useCallback(() => {
    fetchPortfolioPrices();
  }, [fetchPortfolioPrices]);

  // Cancel any ongoing requests when component unmounts
  useEffect(() => {
    return () => {
      unifiedApiService.cancelRequests('portfolio-sync');
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
    };
  }, []);

  return {
    refreshPrices,
    isRefreshing: portfolio ? usePortfolioStore.getState().isLoading : false,
  };
};